import { useMemo } from 'react';
import { useRouter } from 'next/router';
import { omit } from 'lodash-es';

import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { AVAILABILITY_TABLE_TABS } from 'components/availabilityTable/AvailabilityTableFilters/constants';
import {
  getTabSalesProcurementStage,
  getTabSalesReservationType,
  getTabSalesStatus,
} from '@helpers';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectAvailabilityPinnedFilters,
  togglePinnedFiltersState,
} from '@store';

const getFilterValue = (queryParam, data = []) => {
  return (
    queryParam
      ?.split(',')
      ?.map((item) =>
        data.find((option) => option.value.toString() === item.toString())
      ) || []
  ).filter(Boolean);
};

export const useAvailabilityFilterConfig = (enums = {}) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const pinnedFilters = useSelector(selectAvailabilityPinnedFilters);

  const {
    statuses,
    brands,
    bodyTypes,
    skuTypes,
    colors,
    transmissions,
    fuelTypes,
    engineTypes,
    reservationsTypes,
    servicePoints,
    procurementStages,
    sourcingTypes,
    categoryClasses,
    conditions,
  } = enums;

  const locationOptions = useMemo(() => {
    const safe = Array.isArray(servicePoints) ? servicePoints : [];
    return safe.filter(
      (opt) =>
        opt &&
        opt.value !== null &&
        opt.value !== undefined &&
        String(opt.value).trim() !== '-' &&
        String(opt.value).trim() !== ''
    );
  }, [servicePoints]);

  const brandFilterValue = getFilterValue(router.query?.brand, brands) || [];
  const models = brandFilterValue.reduce((acc, cur) => {
    acc = [...acc, ...cur.models];
    return acc;
  }, []);

  const isSalesTab = useMemo(
    () => router.query.tab === AVAILABILITY_TABLE_TABS.sales.key,
    [router.query?.tab]
  );

  const filterCategories = useMemo(
    () => [
      {
        id: 'brand',
        label: 'Brands',
        icon: RadioButtonUncheckedIcon,
        options: brands,
        multiSelect: true,
      },
      {
        id: 'model',
        label: 'Models',
        icon: RadioButtonUncheckedIcon,
        options: models,
        multiSelect: true,
      },
      {
        id: 'transmission',
        label: 'Transmission',
        icon: RadioButtonUncheckedIcon,
        options: transmissions,
        multiSelect: true,
      },
      {
        id: 'color',
        label: 'Color',
        icon: RadioButtonUncheckedIcon,
        options: colors,
        multiSelect: true,
      },
      {
        id: 'procurementStageId',
        label: 'Stage',
        icon: RadioButtonUncheckedIcon,
        options: procurementStages,
        multiSelect: true,
        disabled: isSalesTab,
      },
      {
        id: 'engineType',
        label: 'Engine Type',
        icon: RadioButtonUncheckedIcon,
        options: engineTypes,
        multiSelect: true,
      },
      {
        id: 'fuelType',
        label: 'Fuel Type',
        icon: RadioButtonUncheckedIcon,
        options: fuelTypes,
        multiSelect: true,
      },
      {
        id: 'skuType',
        label: 'Vehicle Type',
        icon: RadioButtonUncheckedIcon,
        options: skuTypes,
        multiSelect: true,
      },
      {
        id: 'bodyType',
        label: 'Body Type',
        icon: RadioButtonUncheckedIcon,
        options: bodyTypes,
        multiSelect: true,
      },
      {
        id: 'reservationType',
        label: 'Reservation Type',
        icon: RadioButtonUncheckedIcon,
        options: reservationsTypes,
        multiSelect: true,
        disabled: isSalesTab,
      },
      {
        id: 'status',
        label: 'Status',
        icon: RadioButtonUncheckedIcon,
        options: statuses,
        multiSelect: true,
        disabled: isSalesTab,
      },
      {
        id: 'location',
        label: 'Location',
        icon: RadioButtonUncheckedIcon,
        options: locationOptions,
        multiSelect: true,
      },
      {
        id: 'sourcingType',
        label: 'Sourcing Type',
        icon: RadioButtonUncheckedIcon,
        options: sourcingTypes,
        multiSelect: true,
      },
      {
        id: 'categoryClass',
        label: 'Category Class',
        icon: RadioButtonUncheckedIcon,
        options: categoryClasses,
        multiSelect: true,
      },
      {
        id: 'condition',
        label: 'Condition',
        icon: RadioButtonUncheckedIcon,
        options: conditions,
        multiSelect: true,
      },
    ],
    [
      bodyTypes,
      brands,
      categoryClasses,
      colors,
      engineTypes,
      fuelTypes,
      models,
      procurementStages,
      reservationsTypes,
      locationOptions,
      skuTypes,
      sourcingTypes,
      statuses,
      transmissions,
      isSalesTab,
      conditions,
    ]
  );

  const handlePinnedFilterSelect = (pinnedFilter) => {
    dispatch(togglePinnedFiltersState({ board: 'availability', pinnedFilter }));
  };
  const hasFilters =
    pinnedFilters?.length > 0 ||
    Object.keys(omit(router.query, ['page', 'pageSize', 'order', 'orderBy']))
      .length > 0;

  const currentFilters = useMemo(() => {
    const parseQueryValue = (value) => {
      if (!value) return [];
      if (typeof value === 'string') {
        return value.split(',').filter(Boolean);
      }
      return Array.isArray(value) ? value : [];
    };

    return {
      brand: parseQueryValue(router.query.brand),
      model: parseQueryValue(router.query.model),
      transmission: parseQueryValue(router.query.transmission),
      color: parseQueryValue(router.query.color),
      procurementStageId: isSalesTab
        ? getTabSalesProcurementStage(procurementStages).map((stage) =>
            String(stage.value)
          )
        : parseQueryValue(router.query.procurementStageId),
      engineType: parseQueryValue(router.query.engineType),
      fuelType: parseQueryValue(router.query.fuelType),
      skuType: parseQueryValue(router.query.skuType),
      bodyType: parseQueryValue(router.query.bodyType),
      reservationType: isSalesTab
        ? getTabSalesReservationType(reservationsTypes).map((type) =>
            String(type.value)
          )
        : parseQueryValue(router.query.reservationType),
      status: isSalesTab
        ? getTabSalesStatus(statuses).map((status) => String(status.value))
        : parseQueryValue(router.query.status),
      location: parseQueryValue(router.query.location),
      sourcingType: parseQueryValue(router.query.sourcingType),
      categoryClass: parseQueryValue(router.query.categoryClass),
      condition: parseQueryValue(router.query.condition),
    };
  }, [
    router.query,
    procurementStages,
    reservationsTypes,
    statuses,
    isSalesTab,
  ]);

  return {
    filterCategories,
    currentFilters,
    handlePinnedFilterSelect,
    pinnedFilters,
    supportPinnedFilters: true,
    hasFilters,
  };
};
